import type { ConstantsSessionStruct } from "@gosaas/types";
import {
	IconDeviceDesktop,
	IconDeviceLaptop,
	IconDeviceMobile,
	IconDevices,
	IconLogout,
} from "@tabler/icons-react";
import { toast } from "sonner";
import { useFetchSessions } from "@/components/queries/security";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

/** Human-friendly device label from a raw user agent string. */
export function describeUserAgent(userAgent: string): string {
	if (!userAgent) return "Unknown device";
	const ua = userAgent.toLowerCase();

	let os = "";
	if (ua.includes("iphone") || ua.includes("ipad")) os = "iOS";
	else if (ua.includes("android")) os = "Android";
	else if (ua.includes("mac os") || ua.includes("macintosh")) os = "macOS";
	else if (ua.includes("windows")) os = "Windows";
	else if (ua.includes("linux")) os = "Linux";

	let browser = "";
	if (ua.includes("edg/")) browser = "Edge";
	else if (ua.includes("opr/") || ua.includes("opera")) browser = "Opera";
	else if (ua.includes("chrome/")) browser = "Chrome";
	else if (ua.includes("firefox/")) browser = "Firefox";
	else if (ua.includes("safari/")) browser = "Safari";

	if (browser && os) return `${browser} on ${os}`;
	if (browser) return browser;
	if (os) return os;
	return userAgent.length > 48 ? `${userAgent.slice(0, 48)}…` : userAgent;
}

export function sessionDeviceIcon(userAgent: string) {
	const ua = (userAgent || "").toLowerCase();
	if (
		ua.includes("iphone") ||
		ua.includes("android") ||
		ua.includes("mobile")
	) {
		return IconDeviceMobile;
	}
	if (ua.includes("mac os") || ua.includes("macintosh")) {
		return IconDeviceLaptop;
	}
	if (ua.includes("windows") || ua.includes("linux")) {
		return IconDeviceDesktop;
	}
	return IconDevices;
}

export function formatSessionDate(value?: string): string {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";
	return date.toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

function SessionRow({
	session,
	onRevoke,
}: {
	session: ConstantsSessionStruct;
	onRevoke: (session: ConstantsSessionStruct) => Promise<void>;
}) {
	const DeviceIcon = sessionDeviceIcon(session.user_agent ?? "");
	return (
		<div className="flex items-center justify-between gap-4 rounded-lg border p-4">
			<div className="flex items-center gap-3 min-w-0">
				<div className="p-2 bg-primary/10 rounded-lg shrink-0">
					<DeviceIcon className="h-5 w-5 text-primary" aria-hidden />
				</div>
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<p className="font-medium truncate">
							{describeUserAgent(session.user_agent ?? "")}
						</p>
						{session.current && <Badge variant="secondary">This device</Badge>}
					</div>
					<p className="text-sm text-muted-foreground truncate">
						{session.ip_address || "Unknown IP"} · Signed in{" "}
						{formatSessionDate(session.created_at)} · Last active{" "}
						{formatSessionDate(session.last_used_at)}
					</p>
				</div>
			</div>
			{!session.current && (
				<Button
					variant="outline"
					size="sm"
					onClick={() => onRevoke(session)}
					aria-label="Revoke session"
				>
					<IconLogout className="h-4 w-4 mr-1" aria-hidden />
					Revoke
				</Button>
			)}
		</div>
	);
}

export function ActiveSessionsCard() {
	const { sessions, isLoadingSessions, refetchSessions } = useFetchSessions();

	const revokeSession = async (session: ConstantsSessionStruct) => {
		try {
			await requestWithHandling<void>(() =>
				apiClient.api.securitySessionsDelete(session.id ?? ""),
			);
			toast.success("Session revoked", {
				description: "That device has been signed out.",
			});
			await refetchSessions();
		} catch (error) {
			toast.error("Couldn't revoke the session", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const revokeOtherSessions = async () => {
		try {
			const response = await requestWithHandling<{ revoked_count: number }>(
				() => apiClient.api.securitySessionsRevokeAllCreate(),
			);
			const count = response.data?.revoked_count ?? 0;
			toast.success("Signed out everywhere else", {
				description:
					count === 1
						? "1 other session was revoked."
						: `${count} other sessions were revoked.`,
			});
			await refetchSessions();
		} catch (error) {
			toast.error("Couldn't sign out other sessions", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	const otherSessionCount = (sessions ?? []).filter((s) => !s.current).length;

	return (
		<Card className="shadow-sm">
			<CardHeader className="pb-4">
				<div className="flex items-start justify-between gap-4">
					<div>
						<CardTitle className="flex items-center gap-2">
							<IconDevices className="h-5 w-5" aria-hidden />
							Active Sessions
						</CardTitle>
						<CardDescription className="mt-1.5">
							Devices currently signed in to your account. Revoke any session
							you don't recognize.
						</CardDescription>
					</div>
					<Button
						variant="outline"
						size="sm"
						onClick={revokeOtherSessions}
						disabled={otherSessionCount === 0}
					>
						Sign out other devices
					</Button>
				</div>
			</CardHeader>
			<CardContent className="pt-0 space-y-3">
				{isLoadingSessions && (
					<>
						<Skeleton className="h-16 w-full" />
						<Skeleton className="h-16 w-full" />
					</>
				)}
				{!isLoadingSessions && (sessions ?? []).length === 0 && (
					<p className="text-sm text-muted-foreground">
						No active sessions found.
					</p>
				)}
				{!isLoadingSessions &&
					(sessions ?? []).map((session) => (
						<SessionRow
							key={session.id}
							session={session}
							onRevoke={revokeSession}
						/>
					))}
			</CardContent>
		</Card>
	);
}
